import React, { useCallback, useEffect, useMemo, useState } from "react";

// region Libraries
import Loading from "react-fullscreen-loading";
import { Badge, Button, MenuItem, TextField } from "@material-ui/core";
import WarningIcon from "@material-ui/icons/ReportProblemOutlined";
import FilterListIcon from "@material-ui/icons/FilterList";
import TuneIcon from "@material-ui/icons/Tune";
import { enUS, es, ptBR } from "date-fns/locale";
// endregion Libraries
// region Languages
import useTranslation from "src/translations/useTranslation";
import { CanConsumptionMessages, FilterMessages } from "@shared/languages/interfaces";
// endregion Languages
// region Services
import api from "@services/api";
// endregion Services
// region Hooks
import { useToast } from "@hooks/useToast";
import usePersistedState from "@hooks/usePersistedState";
// endregion Hooks
// region Utils
import utils from "@utils/useful-functions";
import {
  GAUGE_METRIC_KEYS,
  HeatShade,
  MIN_TRAVELS_FOR_RANKING,
  MetricDescriptor,
  NO_VALUE,
  RANKED_METRICS,
  RankedMetricKey,
  ThresholdMap,
  buildMetricScales,
  defaultThresholds,
  describeFilterSelection,
  formatHoursAndMinutes,
  formatMetricValue,
  heatShadeOf,
  isAttentionShade,
  isFiniteNumber,
  median,
  metricByKey,
  pickPrimaryMetric,
  qualifiesForRanking,
  resolveDayBar,
  seriesUsesVolume,
  sortByWorstFirst,
  zoneOf
} from "./can-consumption-metrics";
import {
  DashboardPeriod,
  defaultPeriod,
  formatDayLabel,
  formatPeriodLabel,
  toRequestRange
} from "./can-consumption-period";
import {
  CanConsumptionDashboardFilter,
  CanConsumptionDashboardFilterOption,
  CanConsumptionDashboardRequest,
  CanConsumptionDashboardResponse,
  EMPTY_DASHBOARD
} from "./can-consumption.types";
// endregion Utils
// region Components
import QueryFilterCanConsumptionDashboard from "./components/QueryFilterCanConsumptionDashboard";
import GaugeCard from "./components/GaugeCard";
import TotalsTiles, { TileData } from "./components/TotalsTiles";
import VehicleRanking, { RankingRow } from "./components/VehicleRanking";
import DailySeriesChart, { DayColumn } from "./components/DailySeriesChart";
import PlantTotalsTable from "./components/PlantTotalsTable";
import VehiclePerformanceTable from "./components/VehiclePerformanceTable";
import AlertThresholdsModal from "./components/AlertThresholdsModal";
// endregion Components
// region Styles
import { Container } from "./styles";
// endregion Styles

/** Alert limits are per browser, like the other dashboard preferences of the platform. */
const ALERT_LIMITS_STORAGE_KEY = "@Fleet:canConsumptionAlertLimits";

const MINUTES_PER_HOUR = 60;

/**
 * The gap's engine hours arrive in hours, and the "h:mm" formatter reads minutes.
 *
 * Zero survives on purpose: a fleet that shuts the engine off in the yard legitimately measures no
 * hours between travels, and it has to read "0:00" rather than the dash of an absent metric.
 */
const hoursToMinutes = (hours: number | null): number | null => (
  isFiniteNumber(hours) ? hours * MINUTES_PER_HOUR : null
);

const ZONE_COLOR = { ok: "#57bb8a", warning: "#fdb56b", critical: "#e8705f" };
const ZONE_MESSAGE = {
  ok: CanConsumptionMessages.zoneOk,
  warning: CanConsumptionMessages.zoneWarn,
  critical: CanConsumptionMessages.zoneCritical
};

/**
 * Fuel consumption panel built on `vehicles/get-can-consumption-dashboard`.
 *
 * Every filter is applied by the endpoint, so the fleet line is always recomputed from the totals of
 * the selection — never from an average of the rows on screen.
 */
const CanConsumptionDashboard: React.FC = () => {

  /* region Hooks */
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  /* endregion Hooks */
  /* region States */
  const [period, setPeriod] = useState<DashboardPeriod>(() => defaultPeriod());
  const [selectedPlants, setSelectedPlants] = useState<CanConsumptionDashboardFilterOption[]>([]);
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<CanConsumptionDashboardFilterOption[]>([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [dashboard, setDashboard] = useState<CanConsumptionDashboardResponse>(EMPTY_DASHBOARD);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [rankMetricKey, setRankMetricKey] = useState<RankedMetricKey>("litersPerM3");
  const [openAlertLimits, setOpenAlertLimits] = useState(false);
  const [storedThresholds, setStoredThresholds] = usePersistedState<ThresholdMap | null>(ALERT_LIMITS_STORAGE_KEY, null);
  /* endregion States */
  /* region Constants */
  const decimalSeparator: "." | "," = i18n.language === "en-US" ? "." : ",";
  const dateLocale = i18n.language === "en-US" ? enUS : (i18n.language === "es" ? es : ptBR);

  const value = useCallback(
    (metric: number | null, fractionDigits: number) => formatMetricValue(metric, fractionDigits, decimalSeparator),
    [decimalSeparator]
  );
  /* endregion Constants */
  /* region Derived data */
  const scales = useMemo(() => buildMetricScales(dashboard.vehicles), [dashboard.vehicles]);
  const defaults = useMemo(() => defaultThresholds(scales), [scales]);
  const thresholds = useMemo<ThresholdMap>(() => ({ ...defaults, ...(storedThresholds ?? {}) }), [defaults, storedThresholds]);

  const alertLimitsChanged = useMemo(() => RANKED_METRICS.some((descriptor) => (
    thresholds[descriptor.key].warn !== defaults[descriptor.key].warn
    || thresholds[descriptor.key].critical !== defaults[descriptor.key].critical
  )), [thresholds, defaults]);

  const primaryMetric = useMemo(
    () => pickPrimaryMetric(dashboard.vehicles, rankMetricKey),
    [dashboard.vehicles, rankMetricKey]
  );

  const shadeOf = useCallback((metric: number | null, descriptor: MetricDescriptor): HeatShade | null => (
    heatShadeOf(metric, descriptor, scales[descriptor.key], thresholds[descriptor.key])
  ), [scales, thresholds]);

  const rankedVehicles = useMemo(
    () => sortByWorstFirst(dashboard.vehicles.filter((vehicle) => qualifiesForRanking(vehicle)), primaryMetric),
    [dashboard.vehicles, primaryMetric]
  );

  const attentionCount = useMemo(
    () => rankedVehicles.filter((vehicle) => isAttentionShade(shadeOf(vehicle[primaryMetric.key], primaryMetric))).length,
    [rankedVehicles, primaryMetric, shadeOf]
  );

  const hasConsolidatingDays = dashboard.dailySeries.some((day) => day.isConsolidating);

  const appliedFilter = useMemo<CanConsumptionDashboardFilter>(() => ({
    period,
    origins: selectedPlants,
    vehicleTypes: selectedVehicleTypes
  }), [period, selectedPlants, selectedVehicleTypes]);

  /** Same count the data tables of the platform show: filled fields, not selected values. */
  const appliedFilterCount = (selectedPlants.length > 0 ? 1 : 0) + (selectedVehicleTypes.length > 0 ? 1 : 0);

  /** What the filter kept, said on the header line so the cut is visible without opening the drawer. */
  const appliedFilterSummary = useMemo(() => [
    describeFilterSelection(selectedPlants, t(CanConsumptionMessages.filterPlantsPlural)),
    describeFilterSelection(selectedVehicleTypes, t(CanConsumptionMessages.filterVehicleTypesPlural))
  ].filter((part): part is string => part !== null).join(" · "), [selectedPlants, selectedVehicleTypes, t]);
  /* endregion Derived data */
  /* region Functions */
  /**
   * Apply what the filter drawer collected; the effect below reloads the panel.
   * @param filter Filter the drawer applied
   */
  const handleApplyFilter = (filter: CanConsumptionDashboardFilter) => {
    setPeriod(filter.period);
    setSelectedPlants(filter.origins);
    setSelectedVehicleTypes(filter.vehicleTypes);
  };

  const loadDashboard = useCallback(async () => {
    setLoadingDashboard(true);

    try {
      const request: CanConsumptionDashboardRequest = {
        ...toRequestRange(period),
        ...(selectedPlants.length > 0 ? { idOrigins: selectedPlants.map((plant) => plant.id) } : {}),
        ...(selectedVehicleTypes.length > 0 ? { idVehicleTypes: selectedVehicleTypes.map((type) => type.id) } : {})
      };

      const { data } = await api.post("vehicles/get-can-consumption-dashboard", request);

      if (data.status === "error") {
        addToast({ type: "error", title: t(CanConsumptionMessages.title), description: data.message });
        setDashboard(EMPTY_DASHBOARD);

        return;
      }

      // An "alert" answer means the filter matched nothing: an empty panel, not a failure.
      setDashboard(data.result ?? EMPTY_DASHBOARD);
    } catch (error: any) {
      utils.handleStandardError(error, t, addToast);
      setDashboard(EMPTY_DASHBOARD);
    } finally {
      setLoadingDashboard(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, selectedPlants, selectedVehicleTypes]);

  /* endregion Functions */
  /* region Effects */
  useEffect(() => {
    loadDashboard().then();
  }, [loadDashboard]);
  /* endregion Effects */
  /* region Render data */
  const tiles: TileData[] = useMemo(() => {
    const fleet = dashboard.fleet;
    const share = (part: number | null): string | undefined => (
      isFiniteNumber(part) && isFiniteNumber(fleet.fuelLiters) && fleet.fuelLiters > 0
        ? value((part / fleet.fuelLiters) * 100, 1)
        : undefined
    );
    const insideShare = share(fleet.fuelInsideTravelsLiters);
    const betweenShare = share(fleet.fuelBetweenTravelsLiters);

    return [
      {
        key: "volume",
        label: t(CanConsumptionMessages.tileVolume),
        value: value(fleet.volumeM3, 0),
        unit: "m³",
        foot: t(CanConsumptionMessages.tileVolumeFoot, { travels: value(fleet.travelsCount, 0) })
      },
      {
        key: "fuel",
        label: t(CanConsumptionMessages.tileFuel),
        value: value(fleet.fuelLiters, 0),
        unit: "L",
        foot: isFiniteNumber(fleet.distanceKm)
          ? t(CanConsumptionMessages.tileFuelFoot, { distance: value(fleet.distanceKm, 0) })
          : undefined
      },
      {
        key: "fuel-inside",
        label: t(CanConsumptionMessages.tileFuelInside),
        value: value(fleet.fuelInsideTravelsLiters, 0),
        unit: "L",
        foot: insideShare ? t(CanConsumptionMessages.tileFuelInsideFoot, { percent: insideShare }) : undefined
      },
      {
        key: "fuel-between",
        label: t(CanConsumptionMessages.tileFuelBetween),
        value: value(fleet.fuelBetweenTravelsLiters, 0),
        unit: "L",
        foot: betweenShare ? t(CanConsumptionMessages.tileFuelBetweenFoot, { percent: betweenShare }) : undefined,
        // "Between travels" reads as "in the yard", and the number is every litre the counter advanced
        // outside a travel — the trip to the workshop and to the pump included.
        hint: t(CanConsumptionMessages.tileFuelBetweenHint)
      },
      {
        key: "hours-between",
        label: t(CanConsumptionMessages.tileHoursBetween),
        value: formatHoursAndMinutes(hoursToMinutes(fleet.engineHoursBetweenTravels)),
        unit: "h:mm",
        foot: isFiniteNumber(fleet.idleEngineHoursBetweenTravels)
          ? t(CanConsumptionMessages.tileHoursBetweenFoot, {
            idle: formatHoursAndMinutes(hoursToMinutes(fleet.idleEngineHoursBetweenTravels))
          })
          : undefined,
        hint: t(CanConsumptionMessages.tileHoursBetweenHint)
      },
      {
        key: "km-per-travel",
        label: t(CanConsumptionMessages.tileAvgKmPerTravel),
        value: value(fleet.avgKmPerTravel, 1),
        unit: "km",
        foot: t(CanConsumptionMessages.tileAvgKmPerTravelFoot)
      },
      {
        key: "avg-speed",
        label: t(CanConsumptionMessages.tileAvgSpeed),
        value: value(fleet.avgSpeedKmh, 1),
        unit: "km/h",
        foot: t(CanConsumptionMessages.tileAvgSpeedFoot)
      },
      {
        key: "avg-travel-time",
        label: t(CanConsumptionMessages.tileAvgTravelTime),
        value: formatHoursAndMinutes(fleet.avgTravelMinutes),
        unit: "h:mm",
        foot: t(CanConsumptionMessages.tileAvgTravelTimeFoot)
      },
      {
        key: "attention",
        label: t(CanConsumptionMessages.tileAttention),
        value: String(attentionCount),
        foot: t(CanConsumptionMessages.tileAttentionFoot, { metric: t(primaryMetric.shortLabelKey).toLowerCase() })
      }
    ];
  }, [dashboard.fleet, attentionCount, primaryMetric, t, value]);

  const rankingRows: RankingRow[] = useMemo(() => rankedVehicles.map((vehicle) => ({
    key: vehicle.idVehicle,
    name: vehicle.identification,
    value: vehicle[primaryMetric.key] as number,
    formattedValue: value(vehicle[primaryMetric.key], primaryMetric.fractionDigits),
    shade: shadeOf(vehicle[primaryMetric.key], primaryMetric)
  })), [rankedVehicles, primaryMetric, shadeOf, value]);

  const rankingMedian = useMemo(
    () => median(rankedVehicles.map((vehicle) => vehicle[primaryMetric.key])),
    [rankedVehicles, primaryMetric]
  );

  const barsAreVolume = seriesUsesVolume(dashboard.dailySeries);

  // The series carries every day of the period, so a day with no operation keeps its slot and the
  // axis stays continuous — a dropped Sunday would silently turn a seven-day week into six bars.
  const dayColumns: DayColumn[] = useMemo(() => dashboard.dailySeries.map((day) => {
    const bar = resolveDayBar(day, barsAreVolume);
    const withoutBarTitle = bar.hasOperation
      ? `${value(day.travelsCount, 0)} ${t(CanConsumptionMessages.columnTravels).toLowerCase()}`
      : t(CanConsumptionMessages.dailyNoOperation);

    return {
      key: day.date,
      label: formatDayLabel(day.date, dateLocale),
      travelsCount: day.travelsCount,
      barValue: bar.value,
      barLabel: bar.hasBar ? value(bar.value, 0) : NO_VALUE,
      metricLabel: value(day[primaryMetric.key], primaryMetric.fractionDigits),
      hasMetric: isFiniteNumber(day[primaryMetric.key]),
      isConsolidating: day.isConsolidating,
      title: bar.hasBar ? `${value(bar.value, 0)} ${barsAreVolume ? "m³" : "L"}` : withoutBarTitle
    };
  }), [dashboard.dailySeries, barsAreVolume, primaryMetric, dateLocale, t, value]);
  /* endregion Render data */

  return (
    <>
      <Loading loading={loadingDashboard} />
      <Container>
        <div className="toolbar">
          <p className="period-line">
            <b>{formatPeriodLabel(period, dateLocale)}</b>
            {` — ${t(CanConsumptionMessages.periodSummary, {
              vehicles: value(dashboard.vehicles.length, 0),
              travels: value(dashboard.fleet.travelsCount, 0)
            })}`}
            {appliedFilterSummary && ` · ${appliedFilterSummary}`}
          </p>

          <div className="toolbar-actions">
            <Badge color="secondary" variant="dot" invisible={!alertLimitsChanged}>
              <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setOpenAlertLimits(true)}>
                {t(CanConsumptionMessages.filterAlertLimits)}
              </Button>
            </Badge>
            <Badge color="error" badgeContent={appliedFilterCount}>
              <Button variant="contained" startIcon={<FilterListIcon />} onClick={() => setOpenFilter(true)}>
                {t(FilterMessages.filtersTitle)}
              </Button>
            </Badge>
          </div>
        </div>

        {hasConsolidatingDays && (
          <p className="period-warning">
            <WarningIcon fontSize="small" />
            {t(CanConsumptionMessages.periodConsolidatingWarning)}
          </p>
        )}

        <div className="gauges">
          {GAUGE_METRIC_KEYS.map((key) => {
            const descriptor = metricByKey(key);
            const metricValue = dashboard.fleet[key];
            const limits = thresholds[key];
            const zone = zoneOf(metricValue, descriptor, limits);
            const hint = descriptor.higherIsBetter
              ? t(CanConsumptionMessages.hintHigherIsBetter)
              : t(CanConsumptionMessages.hintLowerIsBetter);

            return (
              <GaugeCard
                key={key}
                title={t(descriptor.labelKey)}
                subtitle={`${t(descriptor.unitKey)} · ${hint}`}
                unit={t(descriptor.unitKey)}
                value={metricValue}
                formattedValue={value(metricValue, descriptor.fractionDigits)}
                higherIsBetter={descriptor.higherIsBetter}
                median={scales[key].median}
                warn={limits.warn}
                critical={limits.critical}
                status={zone === null ? null : {
                  color: ZONE_COLOR[zone],
                  text: `${t(ZONE_MESSAGE[zone])} (${t(CanConsumptionMessages.gaugeThresholds, {
                    warn: value(limits.warn, descriptor.fractionDigits),
                    critical: value(limits.critical, descriptor.fractionDigits)
                  })})`
                }}
                noDataText={t(CanConsumptionMessages.gaugeNoData)}
                formatTick={(tick) => value(tick, tick >= 10 ? 0 : 1)}
              />
            );
          })}
        </div>

        <TotalsTiles tiles={tiles} />

        <div className="panel-row">
          <div className="panel">
            <div className="panel-head">
              <div className="panel-head-main">
                <h2>{t(CanConsumptionMessages.rankingTitle)}</h2>
                <p>
                  {t(CanConsumptionMessages.rankingSubtitle)}
                  {primaryMetric.key !== rankMetricKey && ` ${t(CanConsumptionMessages.rankingFallbackNote, {
                    metric: t(metricByKey(rankMetricKey).shortLabelKey).toLowerCase(),
                    fallback: t(primaryMetric.shortLabelKey).toLowerCase()
                  })}`}
                </p>
              </div>
              <TextField
                select
                size="small"
                variant="outlined"
                label={t(CanConsumptionMessages.rankingMetricLabel)}
                value={rankMetricKey}
                onChange={(event) => setRankMetricKey(event.target.value as RankedMetricKey)}
              >
                {RANKED_METRICS.map((descriptor) => (
                  <MenuItem key={descriptor.key} value={descriptor.key}>
                    {`${t(descriptor.shortLabelKey)} (${t(descriptor.unitKey)})`}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div className="panel-body">
              <VehicleRanking
                rows={rankingRows}
                median={rankingMedian}
                unit={t(primaryMetric.unitKey)}
                emptyText={t(CanConsumptionMessages.rankingEmpty, { metric: t(primaryMetric.shortLabelKey).toLowerCase() })}
              />
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <div className="panel-head-main">
                <h2>{t(CanConsumptionMessages.dailyTitle)}</h2>
                <p>{t(CanConsumptionMessages.dailySubtitle)}</p>
              </div>
            </div>
            <div className="panel-body">
              <DailySeriesChart
                days={dayColumns}
                footText={t(CanConsumptionMessages.dailyFoot, {
                  bars: barsAreVolume ? t(CanConsumptionMessages.dailyBarsVolume) : t(CanConsumptionMessages.dailyBarsFuel),
                  metric: t(primaryMetric.shortLabelKey).toLowerCase(),
                  unit: t(primaryMetric.unitKey)
                })}
                consolidatingMark={t(CanConsumptionMessages.dailyConsolidating)}
                consolidatingLegend={t(CanConsumptionMessages.dailyConsolidatingLegend)}
                emptyText={t(CanConsumptionMessages.periodNoData)}
              />
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <div className="panel-head-main">
              <h2>{t(CanConsumptionMessages.tableTitle)}</h2>
            </div>
          </div>
          <VehiclePerformanceTable
            vehicles={dashboard.vehicles}
            scales={scales}
            thresholds={thresholds}
            minTravels={MIN_TRAVELS_FOR_RANKING}
            decimalSeparator={decimalSeparator}
          />
        </div>

        <div className="panel">
          <div className="panel-head">
            <div className="panel-head-main">
              <h2>{t(CanConsumptionMessages.plantsTitle)}</h2>
              <p>{t(CanConsumptionMessages.plantsSubtitle)}</p>
            </div>
          </div>
          <PlantTotalsTable origins={dashboard.origins} decimalSeparator={decimalSeparator} />
        </div>
      </Container>

      <QueryFilterCanConsumptionDashboard
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        appliedFilter={appliedFilter}
        onFilter={handleApplyFilter}
      />

      <AlertThresholdsModal
        open={openAlertLimits}
        onClose={() => setOpenAlertLimits(false)}
        scales={scales}
        thresholds={thresholds}
        onChangeThresholds={setStoredThresholds}
        onRestoreDefaults={() => setStoredThresholds(null)}
        decimalSeparator={decimalSeparator}
      />
    </>
  );
};

export default CanConsumptionDashboard;
